import { IonicSafeString, useIonAlert, useIonLoading } from '@ionic/react'
import { msg } from '@lingui/core/macro'
import { useLingui } from '@lingui/react'
import { useCreation, useLocalStorageState } from 'ahooks'
import { isNil } from 'lodash'
import { useCallback, useRef } from 'react'
import type { GeolocationInput } from '../../../gql/graphql'
import { getAppLocation } from '../../../helpers/geoLocation'
import { useToast } from '../../../hooks/useToast'

const VALID_CACHE_TIME_IN_MS = 1000 * 60

export type GeoActionText = 'clock in' | 'clock out' | 'change task'

type UseGeoLocationProps = {
  actionText: GeoActionText
  showErrorToast?: boolean
  onError?: (message?: string) => void
}

type CachedGeolocation = {
  coords: GeolocationInput
  timestamp: number
}

/**
 * Captures the device location for time clock actions. Ports the v1 crew-app
 * behavior: permission checks, a rationale alert before the OS prompt, error
 * toasts, and a short-lived position cache. Returns coordinates shaped for the
 * GraphQL `GeolocationInput`, so they travel with the (possibly offline-queued)
 * mutation.
 */
export const useGeoLocation = (props: UseGeoLocationProps) => {
  const { _ } = useLingui()
  const lastLocation = useRef<CachedGeolocation | undefined>(undefined)
  const { onError, actionText, showErrorToast } = {
    showErrorToast: true,
    ...props,
  }
  const [presentAlert] = useIonAlert()
  const [presentToast] = useToast()
  const [presentLoading, dismissLoading] = useIonLoading()
  const [hasLocationSucceededPreviously, setHasLocationSucceededPreviously] =
    useLocalStorageState<boolean>('csGeoLocationSucceeded', {
      defaultValue: false,
    })

  const action = {
    'clock in': _(msg`clock in`),
    'clock out': _(msg`clock out`),
    'change task': _(msg`change task`),
  }[actionText]

  const defaultMessage = _(
    msg`Location services are required to ${action}. Please enable location services, or contact your administrator.`,
  )

  const appLocation = useCreation(() => getAppLocation(), [])

  const fetchCurrentPosition =
    useCallback(async (): Promise<CachedGeolocation | null> => {
      if (!appLocation) return null
      try {
        const position = await appLocation.getCurrentPosition({
          enableHighAccuracy: true,
          maximumAge: 0,
        })
        if (!position) return null
        const { timestamp, coords } = position
        if (isNil(coords.latitude) || isNil(coords.longitude)) {
          return null
        }
        setHasLocationSucceededPreviously(true)
        const geolocation: CachedGeolocation = {
          timestamp,
          coords: {
            lat: coords.latitude,
            lng: coords.longitude,
            accuracy: coords.accuracy,
            altitude: coords.altitude,
            altitudeAccuracy: coords.altitudeAccuracy ?? null,
            heading: coords.heading,
            speed: coords.speed,
          },
        }
        if (geolocation.timestamp > (lastLocation.current?.timestamp || 0)) {
          lastLocation.current = geolocation
        }
        return geolocation
      } catch {
        return null
      }
    }, [appLocation, setHasLocationSucceededPreviously])

  const getCurrentCoords = useCallback(
    async (args?: {
      ignoreCache?: boolean
    }): Promise<GeolocationInput | null> => {
      if (
        args?.ignoreCache !== true &&
        lastLocation.current &&
        lastLocation.current.timestamp > Date.now() - VALID_CACHE_TIME_IN_MS
      ) {
        return lastLocation.current.coords
      }

      const onLocationError = (message?: string) => {
        setHasLocationSucceededPreviously(false)
        const errorMessage = message || defaultMessage
        onError?.(errorMessage)
        if (showErrorToast) {
          presentToast({
            color: 'danger',
            duration: 30000,
            message: errorMessage,
          })
        }
      }

      if (!appLocation) {
        onLocationError(
          _(
            msg`Location services are required to ${action}. Please contact your administrator for assistance.`,
          ),
        )
        return null
      }

      const locationPermission = await appLocation.checkPermissions()
      if (locationPermission === 'denied') {
        onLocationError(
          _(
            msg`Location services are required to ${action}, but are disabled. Please enable location services on your device to ${action}.`,
          ),
        )
        return null
      }
      if (locationPermission === 'prompt' && !hasLocationSucceededPreviously) {
        await new Promise<void>((resolve) => {
          presentAlert({
            header: _(msg`Location Required`),
            message: new IonicSafeString(
              _(
                msg`Your organization requires your location in order to ${action}. <br /> <br /> To ${action}, you must Allow your precise location if asked.`,
              ),
            ),
            buttons: [{ text: _(msg`Continue`) }],
            onDidDismiss: () => resolve(),
          })
        })
      }

      await presentLoading()
      const position = await fetchCurrentPosition().finally(() =>
        dismissLoading(),
      )
      if (!position) {
        onLocationError()
        return null
      }
      return position.coords
    },
    [
      _,
      action,
      appLocation,
      defaultMessage,
      dismissLoading,
      fetchCurrentPosition,
      hasLocationSucceededPreviously,
      onError,
      presentAlert,
      presentLoading,
      presentToast,
      setHasLocationSucceededPreviously,
      showErrorToast,
    ],
  )

  const preFetchGeolocation = useCallback(async () => {
    if (!appLocation) return
    const locationPermission = await appLocation.checkPermissions()
    if (locationPermission === 'granted') {
      await fetchCurrentPosition()
    }
  }, [appLocation, fetchCurrentPosition])

  return { getCurrentCoords, preFetchGeolocation }
}
